import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Trash2,
  Users,
  Search,
  RefreshCw,
  DollarSign,
  Building2,
  Percent,
  Copy,
  CheckCircle,
  XCircle,
  Eye,
  Link2 as LinkIcon,
} from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

function PartnersPage() {
  const { t, isRTL } = useLanguage();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [newPartnerLink, setNewPartnerLink] = useState({ name: '', code: '' });
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company_name: '',
    phone: '',
    address: '',
    country: '',
    profit_share: 20,
  });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/superadmin/partners`, {
        withCredentials: true
      });
      setPartners(response.data);
    } catch (error) {
      console.error('Failed to fetch partners:', error);
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPartner = async (e) => {
    e.preventDefault();
    setFormError('');
    
    if (!formData.name || !formData.email || !formData.password) {
      setFormError('Name, email, and password are required');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/superadmin/partners`, formData, {
        withCredentials: true
      });
      toast.success('Partner created successfully');
      setShowAddDialog(false);
      resetForm();
      // Show referral signup link dialog
      const code = res.data?.referral_code;
      if (code) {
        setNewPartnerLink({ name: res.data.name || formData.name, code });
        setShowLinkDialog(true);
      }
      fetchPartners();
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Failed to create partner');
    }
  };

  const handleEditPartner = async (e) => {
    e.preventDefault();
    setFormError('');
    
    try {
      const updateData = {
        name: formData.name,
        company_name: formData.company_name,
        phone: formData.phone,
        address: formData.address,
        country: formData.country,
        profit_share: formData.profit_share,
        status: formData.status,
      };
      
      await axios.put(`${API}/superadmin/partners/${selectedPartner.partner_id}`, updateData, {
        withCredentials: true
      });
      toast.success('Partner updated successfully');
      setShowEditDialog(false);
      fetchPartners();
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Failed to update partner');
    }
  };

  const handleDeletePartner = async () => {
    try {
      await axios.delete(`${API}/superadmin/partners/${selectedPartner.partner_id}`, {
        withCredentials: true
      });
      toast.success('Partner deleted successfully');
      setShowDeleteDialog(false);
      setSelectedPartner(null);
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete partner');
    }
  };

  const handleViewPartner = async (partner) => {
    try {
      const response = await axios.get(`${API}/superadmin/partners/${partner.partner_id}`, {
        withCredentials: true
      });
      setSelectedPartner(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to load partner details');
    }
  };

  const openEditDialog = (partner) => {
    setSelectedPartner(partner);
    setFormData({
      name: partner.name,
      email: partner.email,
      password: '',
      company_name: partner.company_name || '',
      phone: partner.phone || '',
      address: partner.address || '',
      country: partner.country || '',
      profit_share: partner.profit_share || 20,
      status: partner.status || 'active',
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      company_name: '',
      phone: '',
      address: '',
      country: '',
      profit_share: 20,
    });
    setFormError('');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getSignupLink = (code) => `${window.location.origin}/admin/signup?ref=${code}`;

  const filteredPartners = partners.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.referral_code?.includes(searchTerm)
  );

  // Calculate totals
  const totalPartners = partners.length;
  const activePartners = partners.filter(p => p.status === 'active').length;
  const totalCompanies = partners.reduce((sum, p) => sum + (p.total_companies || 0), 0);
  const totalPaidCompanies = partners.reduce((sum, p) => sum + (p.paid_companies || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("nav.partners")}</h1>
          <p className="text-gray-500">Manage affiliate partners and referrals</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchPartners}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => { resetForm(); setShowAddDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add Partner
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Partners</p>
                <p className="text-2xl font-bold">{totalPartners}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Partners</p>
                <p className="text-2xl font-bold">{activePartners}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Companies</p>
                <p className="text-2xl font-bold">{totalCompanies}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Paid Companies</p>
                <p className="text-2xl font-bold">{totalPaidCompanies}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by name, email, or referral code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Partners Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Referral Code</TableHead>
                <TableHead>Partner Name</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead>Profit Share</TableHead>
                <TableHead className="text-center">Total Companies</TableHead>
                <TableHead className="text-center">Paid</TableHead>
                <TableHead className="text-center">Unpaid</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPartners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    No partners found
                  </TableCell>
                </TableRow>
              ) : (
                filteredPartners.map((partner) => (
                  <TableRow key={partner.partner_id}>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <code className="bg-gray-100 px-2 py-1 rounded font-mono text-sm">
                          {partner.referral_code}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          title="Copy referral code"
                          onClick={() => copyToClipboard(partner.referral_code)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs text-indigo-600 hover:text-indigo-700 px-2"
                          title="Copy signup link"
                          onClick={() => copyToClipboard(getSignupLink(partner.referral_code))}
                          data-testid={`copy-link-${partner.referral_code}`}
                        >
                          <LinkIcon className="w-3 h-3 mr-1" />
                          Link
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{partner.name}</p>
                        <p className="text-xs text-gray-500">{partner.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        partner.status === 'active' ? 'bg-green-100 text-green-700' :
                        partner.status === 'suspended' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }>
                        {partner.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-purple-600">
                        <Percent className="w-3 h-3 mr-1" />
                        {partner.profit_share}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center font-medium">
                      {partner.total_companies || 0}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-green-600 font-medium">{partner.paid_companies || 0}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-orange-600 font-medium">{partner.unpaid_companies || 0}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleViewPartner(partner)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(partner)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600"
                          onClick={() => { setSelectedPartner(partner); setShowDeleteDialog(true); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Partner Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Partner</DialogTitle>
            <DialogDescription>
              Create a new affiliate partner account
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddPartner} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Partner name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="partner@example.com"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="Min 6 characters"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  placeholder="Company name"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("common.phone")}</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+1234567890"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Country</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value})}
                  placeholder="Country"
                />
              </div>
              <div className="space-y-2">
                <Label>Profit Share (%)</Label>
                <Input
                  type="number"
                  value={formData.profit_share}
                  onChange={(e) => setFormData({...formData, profit_share: parseFloat(e.target.value) || 20})}
                  min="0"
                  max="100"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="Full address"
              />
            </div>
            {formError && (
              <p className="text-sm text-red-600 bg-red-50 p-2 rounded">{formError}</p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Create Partner</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Partner Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Partner</DialogTitle>
            <DialogDescription>
              Update partner information
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditPartner} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("common.name")}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("common.status")}</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({...formData, status: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t("common.active")}</SelectItem>
                    <SelectItem value="inactive">{t("common.inactive")}</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("common.phone")}</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Country</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Profit Share (%)</Label>
                <Input
                  type="number"
                  value={formData.profit_share}
                  onChange={(e) => setFormData({...formData, profit_share: parseFloat(e.target.value) || 20})}
                  min="0"
                  max="100"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
              />
            </div>
            {formError && (
              <p className="text-sm text-red-600 bg-red-50 p-2 rounded">{formError}</p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Update Partner</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Partner Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Partner Details</DialogTitle>
          </DialogHeader>
          {selectedPartner && (
            <div className="space-y-6">
              {/* Partner Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">{t("common.name")}</p>
                  <p className="font-medium">{selectedPartner.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t("common.email")}</p>
                  <p className="font-medium">{selectedPartner.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Referral Code</p>
                  <code className="bg-gray-100 px-2 py-1 rounded">{selectedPartner.referral_code}</code>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-500 mb-1">Signup Link</p>
                  <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                    <LinkIcon className="w-4 h-4 text-indigo-500 shrink-0" />
                    <code className="text-sm text-indigo-700 break-all flex-1" data-testid="partner-signup-link">
                      {getSignupLink(selectedPartner.referral_code)}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0 text-indigo-600"
                      onClick={() => copyToClipboard(getSignupLink(selectedPartner.referral_code))}
                      data-testid="copy-signup-link-btn"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Profit Share</p>
                  <p className="font-medium">{selectedPartner.profit_share}%</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Earnings</p>
                  <p className="font-medium text-green-600">${selectedPartner.total_earnings?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pending Earnings</p>
                  <p className="font-medium text-orange-600">${selectedPartner.pending_earnings?.toFixed(2)}</p>
                </div>
              </div>

              {/* Referred Companies */}
              {selectedPartner.companies?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Referred Companies ({selectedPartner.companies.length})</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Company</TableHead>
                          <TableHead>{t("common.status")}</TableHead>
                          <TableHead>Country</TableHead>
                          <TableHead>Created</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedPartner.companies.map((company) => (
                          <TableRow key={company.company_id}>
                            <TableCell className="font-medium">{company.name}</TableCell>
                            <TableCell>
                              <Badge className={
                                company.subscription_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                              }>
                                {company.subscription_status || 'free'}
                              </Badge>
                            </TableCell>
                            <TableCell>{company.country || '-'}</TableCell>
                            <TableCell>{new Date(company.created_at).toLocaleDateString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Referral Signup Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Partner Created Successfully
            </DialogTitle>
            <DialogDescription>
              Share this signup link with <strong>{newPartnerLink.name}</strong>'s referrals. Company admins who sign up using this link will be automatically linked to this partner.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-gray-500 uppercase tracking-wide">Referral Signup Link</Label>
              <div className="flex items-center gap-2 mt-1 bg-gray-50 border rounded-lg p-3">
                <LinkIcon className="w-4 h-4 text-indigo-500 shrink-0" />
                <code className="text-sm text-gray-800 break-all flex-1" data-testid="new-partner-signup-link">
                  {getSignupLink(newPartnerLink.code)}
                </code>
              </div>
            </div>
            <Button
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              onClick={() => {
                copyToClipboard(getSignupLink(newPartnerLink.code));
              }}
              data-testid="copy-new-partner-link-btn"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Signup Link
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Partner?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedPartner?.name} and their account.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePartner} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default PartnersPage;
